import React, { useState, useEffect } from 'react';
import { FaTimes, FaHistory, FaCheck, FaTimesCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../../services/api';
import { formatAppDateTime } from '../../../../utils/dateUtils';

const QueryHistory = ({ queryId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.telegramBI.queries.getHistory(queryId, { limit: 50 });
        const data = res.data?.data || res.data || [];
        setHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error('Failed to load history');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [queryId]);

  const successConfig = {
    icon: FaCheck,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    label: 'Completed',
  };
  const failedConfig = {
    icon: FaTimesCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    label: 'Failed',
  };
  const getStatusConfig = (exec) => (exec?.success ? successConfig : failedConfig);

  const executorName = (exec) => {
    const name = [exec?.first_name, exec?.last_name].filter(Boolean).join(' ').trim();
    return exec?.username || name || exec?.chat_record_id || 'System';
  };

  const fmt = (ms) => {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const fmtDate = (d) => (d ? formatAppDateTime(d) : '—');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-surface rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FaHistory className="text-gray-400" />
            <h2 className="text-base font-semibold text-gray-900">Execution History</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* List */}
          <div className="w-1/2 border-r border-gray-100 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">No executions recorded</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {history.map((exec) => {
                  const cfg = getStatusConfig(exec);
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={exec.execution_id}
                      onClick={() => setSelected(exec)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        selected?.execution_id === exec.execution_id
                          ? 'bg-primary-50/50 border-l-2 border-primary-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}
                        >
                          <Icon className="text-[9px]" />
                          {cfg.label}
                        </span>
                        <span className="text-[11px] text-gray-400">{fmt(exec.duration_ms)}</span>
                      </div>
                      <div className="text-xs text-gray-500">{fmtDate(exec.execution_time)}</div>
                      {exec.row_count != null && (
                        <div className="text-[11px] text-gray-400 mt-0.5">
                          {exec.row_count.toLocaleString()} rows
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="w-1/2 overflow-y-auto">
            {selected ? (
              <div className="p-4 space-y-3">
                <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                  Execution #{selected.execution_id}
                </div>

                <div className="space-y-2">
                  {[
                    { label: 'Status', value: getStatusConfig(selected).label },
                    { label: 'Started', value: fmtDate(selected.execution_time) },
                    { label: 'Duration', value: fmt(selected.duration_ms) },
                    { label: 'Rows', value: selected.row_count?.toLocaleString() ?? '—' },
                    { label: 'Cache Hit', value: selected.cache_hit ? 'Yes' : 'No' },
                    { label: 'Executed By', value: executorName(selected) },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between text-sm">
                      <span className="text-gray-500">{label}</span>
                      <span className="text-gray-900 font-medium">{value}</span>
                    </div>
                  ))}
                </div>

                {selected.error_message && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="text-xs font-medium text-red-700 mb-1">Error</div>
                    <div className="text-xs text-red-600 font-mono whitespace-pre-wrap">
                      {selected.error_message}
                    </div>
                  </div>
                )}

                {selected.parameters && (
                  <div className="mt-3">
                    <div className="text-xs font-medium text-gray-500 mb-1">Parameters</div>
                    <pre className="text-xs bg-gray-50 rounded-lg p-2 font-mono text-gray-700 overflow-x-auto">
                      {typeof selected.parameters === 'string'
                        ? selected.parameters
                        : JSON.stringify(selected.parameters, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-gray-400">
                Select an execution to view details
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-surface border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueryHistory;
